import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, Heart, Gift, XCircle, Clock, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const TrialSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [trialData, setTrialData] = useState(null);
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 2000;

    const pollStatus = async () => {
      try {
        const res = await axios.get(`${API}/trial/payment-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setTrialData(res.data);

        if (res.data.status === 'success') {
          setStatus('success');
          await refreshUser();
          return;
        } else if (res.data.status === 'expired') {
          setStatus('error');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, pollInterval);
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error('Error checking trial status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    pollStatus();
  }, [sessionId, token, refreshUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-card rounded-3xl p-8 text-center border border-border shadow-xl"
      >
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-accent animate-spin" />
            </div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Aktiverer prøveperiode...
            </h2>
            <p className="text-muted-foreground">
              Vent venligst mens vi konfigurerer din konto...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="w-20 h-20 rounded-full bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center mx-auto mb-6"
            >
              <Gift className="w-10 h-10 text-white" />
            </motion.div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Din prøveperiode er aktiveret!
            </h2>
            <p className="text-muted-foreground mb-6">
              Du har nu 3 dages gratis adgang til alle premium-funktioner.
            </p>
            
            <div className="bg-muted/50 rounded-xl p-4 mb-6 text-left space-y-3">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-accent" />
                <div>
                  <div className="text-sm font-medium text-foreground">Prøveperiode</div>
                  <div className="text-xs text-muted-foreground">3 dage gratis fra nu</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium text-foreground">Automatisk fornyelse</div>
                  <div className="text-xs text-muted-foreground">
                    Dit kort debiteres først efter prøveperioden
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-accent/10 border border-accent/20 rounded-xl p-4 mb-6">
              <p className="text-sm text-foreground">
                <strong>Tip:</strong> Du kan annullere når som helst inden prøveperioden udløber, 
                og du vil ikke blive opkrævet.
              </p>
            </div>

            <div className="space-y-3">
              <Link to="/discover" className="block">
                <Button className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="start-exploring-btn">
                  <Heart className="w-5 h-5 mr-2" />
                  Start med at udforske
                </Button>
              </Link>
              <Link to="/settings" className="block">
                <Button variant="outline" className="w-full rounded-full">
                  Administrer abonnement
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-destructive" />
            </div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Noget gik galt
            </h2>
            <p className="text-muted-foreground mb-6">
              Vi kunne ikke aktivere din prøveperiode. Prøv venligst igen.
            </p>
            <div className="space-y-3">
              <Link to="/premium" className="block">
                <Button className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="try-again-btn">
                  Prøv igen
                </Button>
              </Link>
              <Link to="/discover" className="block">
                <Button variant="outline" className="w-full rounded-full">
                  Tilbage til appen
                </Button>
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default TrialSuccess;
